/* eslint-disable max-lines */
/* eslint-disable prefer-object-spread */
import axios from 'axios';
import SparkMD5 from 'spark-md5';
import CryptoJS from 'crypto-js';
import { SALES } from '../constants';
import { getRuntimePlatform, isCordovaFn } from '../components/commonCode';

export const uploadDocument = async (
  fileFormat,
  fileName,
  fileType,
  formData,
  accessToken,
  setStatementUploadProgress,
  setStatementLinearProgress,
  initiateUiResponse,
  options = {}
) => {
  const { commonDocUpload = false } = options;
  let response = null;
  try {
    response = await axios.post(
      `${initiateUiResponse.REACT_APP_DOCUMENT_UPLOAD_URL}?format=${fileFormat}&name=${fileName}&type=${fileType}`,
      formData,
      {
        headers: {
          appid: 'sales',
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setStatementUploadProgress((prev) =>
            commonDocUpload ? { ...prev, [fileType]: percentCompleted } : percentCompleted
          );

          if (percentCompleted === 100) {
            setStatementLinearProgress((prev) =>
              commonDocUpload ? { ...prev, [fileType]: true } : true
            );
          }
        },
      }
    );
  } catch (e) {
    return response;
  }
  return response;
};

export const uploadDocumentXL = async (
  fileFormat,
  fileName,
  fileType,
  fileSize,
  fileMd5Base64,
  accessToken,
  setStatementUploadProgress,
  setStatementLinearProgress,
  initiateUiResponse,
  options = {}
) => {
  const { commonDocUpload = false } = options;
  let response = null;
  const requestBody = {
    format: fileFormat,
    name: fileName,
    type: fileType,
    size: fileSize,
    contentMD5: fileMd5Base64,
  };
  try {
    response = await axios.post(
      `${initiateUiResponse.REACT_APP_DOCUMENT_UPLOAD_XL_URL}`,
      requestBody,
      {
        headers: {
          appid: 'sales',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },

        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setStatementUploadProgress((prev) =>
            commonDocUpload ? { ...prev, [fileType]: percentCompleted } : percentCompleted
          );

          if (percentCompleted === 100) {
            setStatementLinearProgress((prev) =>
              commonDocUpload ? { ...prev, [fileType]: true } : true
            );
          }
        },
      }
    );
  } catch (e) {
    return response;
  }
  return response;
};

// export const uploadDocumentByPreSignedURL = async (
//   preSignedUrl,
//   file,
//   fileMd5Base64,
//   setStatementUploadProgress,
//   setStatementLinearProgress,
//   fileType,
//   options = {}
// ) => {
//   const { commonDocUpload = false } = options;
//   let response = null;
//   try {
//     response = await axios.put(preSignedUrl, file, {
//       headers: {
//         appid: 'sales',
//         'Content-Type': 'multipart/form-data',
//         'Content-MD5': fileMd5Base64,
//       },
//       onUploadProgress: (progressEvent) => {
//         const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
//         setStatementUploadProgress((prev) =>
//           commonDocUpload ? { ...prev, [fileType]: percentCompleted } : percentCompleted
//         );

//         if (percentCompleted === 100) {
//           setStatementLinearProgress((prev) =>
//             commonDocUpload ? { ...prev, [fileType]: true } : true
//           );
//         }
//       },
//     });
//   } catch (e) {
//     return response;
//   }
//   return response;
// };
const IOS_CORDOVA_LARGE_FILE_THRESHOLD = 3145728;
const IOS_NATIVE_TIMEOUT_SECONDS = 60;
const IOS_NATIVE_FALLBACK_TIMEOUT_MS = 65000;
const IOS_ABORT_CONFIRMATION_TIMEOUT_MS = 5000;
const IOS_AXIOS_TIMEOUT_MS = 30000;
const IOS_FETCH_TIMEOUT_MS = 30000;

const isIOSCordovaLargePreSignedUpload = (file) => {
  if (typeof window === 'undefined' || !window.cordova) {
    return false;
  }

  const platform = window.device?.platform || window.cordova.platformId;

  return platform?.toLowerCase() === 'ios' && Number(file?.size) > IOS_CORDOVA_LARGE_FILE_THRESHOLD;
};

const getIOSCordovaHttpPlugin = () =>
  typeof window === 'undefined' ? null : window.cordova?.plugin?.http;

const logIOSCordovaXLUpload = (event, details = {}) => {
  console.log(`[iOS Cordova XL upload] ${event}`, details);
};

const createPreSignedUploadHeaders = (contentType, fileMd5Base64) => ({
  appid: 'sales',
  'Content-Type': contentType,
  'Content-MD5': fileMd5Base64,
});

const readFileAsArrayBuffer = async (file) => {
  // eslint-disable-next-line no-underscore-dangle
  const iosBuffer = file?.__iosArrayBuffer;

  if (iosBuffer instanceof ArrayBuffer && iosBuffer.byteLength > 0) {
    return iosBuffer;
  }

  if (typeof file?.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

const shouldRetryWithAnotherTransport = (status) => !Number.isFinite(status) || status <= 0;

const uploadIOSCordovaViaNativeHttp = async (preSignedUrl, file, fileMd5Base64, contentType) => {
  const http = getIOSCordovaHttpPlugin();

  if (!http || typeof http.sendRequest !== 'function') {
    logIOSCordovaXLUpload('Native HTTP plugin unavailable. Trying Axios fallback.', {
      standardPluginAvailable: Boolean(http),
      alternatePluginAvailable: Boolean(window?.cordova?.plugins?.http),
    });
    return { response: null, shouldFallback: true };
  }

  let body;
  try {
    body = await readFileAsArrayBuffer(file);
  } catch (error) {
    logIOSCordovaXLUpload('Native HTTP body preparation failed. Trying Axios fallback.', {
      fileSize: file?.size,
    });
    return { response: null, shouldFallback: true };
  }

  if (!(body instanceof ArrayBuffer) || body.byteLength === 0) {
    logIOSCordovaXLUpload('Native HTTP body is not a usable ArrayBuffer. Trying Axios fallback.', {
      bodyType: Object.prototype.toString.call(body),
      bodyByteLength: body?.byteLength || 0,
    });
    return { response: null, shouldFallback: true };
  }

  logIOSCordovaXLUpload('Starting native raw PUT.', {
    fileSize: file?.size,
    bodyByteLength: body.byteLength,
    contentType,
    serializer: 'raw',
    timeoutSeconds: IOS_NATIVE_TIMEOUT_SECONDS,
  });

  return new Promise((resolve) => {
    let settled = false;
    let abortRequested = false;
    let requestId;
    let fallbackTimer;
    let abortTimer;
    const finish = (result) => {
      if (!settled) {
        settled = true;
        clearTimeout(fallbackTimer);
        clearTimeout(abortTimer);
        resolve(result);
      }
    };
    const stopNativeRequestBeforeFallback = () => {
      if (settled) return;

      if (typeof http.abort !== 'function' || requestId === undefined) {
        logIOSCordovaXLUpload(
          'Native raw PUT timed out but could not be aborted. Not starting another PUT.'
        );
        finish({ response: null, shouldFallback: false });
        return;
      }

      abortRequested = true;
      logIOSCordovaXLUpload('Native raw PUT timed out. Requesting abort before Axios fallback.', {
        requestId,
      });
      abortTimer = setTimeout(() => {
        logIOSCordovaXLUpload('Native raw PUT abort was not confirmed. Not starting another PUT.');
        finish({ response: null, shouldFallback: false });
      }, IOS_ABORT_CONFIRMATION_TIMEOUT_MS);

      try {
        http.abort(
          requestId,
          (result) => {
            if (settled) return;

            if (result?.aborted === true) {
              logIOSCordovaXLUpload('Native raw PUT aborted. Trying Axios fallback.', {
                requestId,
              });
              finish({ response: null, shouldFallback: true });
              return;
            }

            logIOSCordovaXLUpload(
              'Native raw PUT was not confirmed aborted. Not starting another PUT.',
              {
                requestId,
              }
            );
            finish({ response: null, shouldFallback: false });
          },
          () => {
            if (settled) return;

            logIOSCordovaXLUpload('Native raw PUT abort failed. Not starting another PUT.', {
              requestId,
            });
            finish({ response: null, shouldFallback: false });
          }
        );
      } catch (error) {
        logIOSCordovaXLUpload('Native raw PUT abort threw. Not starting another PUT.', {
          requestId,
        });
        finish({ response: null, shouldFallback: false });
      }
    };
    fallbackTimer = setTimeout(stopNativeRequestBeforeFallback, IOS_NATIVE_FALLBACK_TIMEOUT_MS);

    try {
      requestId = http.sendRequest(
        preSignedUrl,
        {
          method: 'put',
          serializer: 'raw',
          data: body,
          headers: createPreSignedUploadHeaders(contentType, fileMd5Base64),
          responseType: 'text',
          connectTimeout: IOS_NATIVE_TIMEOUT_SECONDS,
          readTimeout: IOS_NATIVE_TIMEOUT_SECONDS,
        },
        (response) => {
          logIOSCordovaXLUpload('Native raw PUT completed.', {
            status: response?.status,
          });
          finish({ response, shouldFallback: false });
        },
        (error) => {
          if (abortRequested) {
            logIOSCordovaXLUpload(
              'Native raw PUT reported cancellation. Waiting for abort confirmation.',
              {
                requestId,
              }
            );
            return;
          }

          const status = Number(error?.status);
          const shouldFallback = shouldRetryWithAnotherTransport(status);

          logIOSCordovaXLUpload(
            shouldFallback
              ? 'Native raw PUT failed before receiving an HTTP response. Trying Axios fallback.'
              : 'Native raw PUT received an HTTP failure response.',
            { status: Number.isFinite(status) ? status : null }
          );

          finish({ response: shouldFallback ? null : { status }, shouldFallback });
        }
      );
    } catch (error) {
      logIOSCordovaXLUpload('Native raw PUT threw before dispatch. Trying Axios fallback.');
      finish({ response: null, shouldFallback: true });
    }
  });
};

const uploadIOSCordovaViaAxios = async (
  preSignedUrl,
  file,
  fileMd5Base64,
  contentType,
  setStatementUploadProgress,
  setStatementLinearProgress,
  fileType,
  commonDocUpload
) => {
  let body;

  try {
    body = await readFileAsArrayBuffer(file);
  } catch (error) {
    logIOSCordovaXLUpload('Axios fallback body preparation failed. Trying fetch fallback.', {
      fileSize: file?.size,
    });
    return { response: null, shouldFallback: true };
  }

  try {
    logIOSCordovaXLUpload('Starting Axios fallback PUT.', {
      bodyByteLength: body.byteLength,
      contentType,
    });

    const response = await axios.put(preSignedUrl, body, {
      headers: createPreSignedUploadHeaders(contentType, fileMd5Base64),
      timeout: IOS_AXIOS_TIMEOUT_MS,
      transformRequest: [(data) => data],
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setStatementUploadProgress((prev) =>
          commonDocUpload ? { ...prev, [fileType]: percentCompleted } : percentCompleted
        );
        if (percentCompleted === 100) {
          setStatementLinearProgress((prev) =>
            commonDocUpload ? { ...prev, [fileType]: true } : true
          );
        }
      },
    });

    logIOSCordovaXLUpload('Axios fallback PUT completed.', { status: response?.status });
    return { response, shouldFallback: false };
  } catch (error) {
    const status = Number(error?.response?.status);
    const shouldFallback = shouldRetryWithAnotherTransport(status);

    logIOSCordovaXLUpload(
      shouldFallback
        ? 'Axios fallback PUT failed before receiving an HTTP response. Trying fetch fallback.'
        : 'Axios fallback PUT received an HTTP failure response.',
      { status: Number.isFinite(status) ? status : null }
    );

    return { response: shouldFallback ? null : { status }, shouldFallback };
  }
};

const uploadIOSCordovaViaFetch = async (preSignedUrl, file, fileMd5Base64, contentType) => {
  if (typeof fetch !== 'function' || typeof AbortController !== 'function') {
    logIOSCordovaXLUpload('Fetch fallback is unavailable.');
    return null;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, IOS_FETCH_TIMEOUT_MS);

  let body;
  try {
    body = await readFileAsArrayBuffer(file);
    logIOSCordovaXLUpload('Starting fetch fallback PUT.', {
      bodyByteLength: body.byteLength,
      contentType,
    });

    const response = await fetch(preSignedUrl, {
      method: 'PUT',
      headers: createPreSignedUploadHeaders(contentType, fileMd5Base64),
      body,
      signal: controller.signal,
    });

    logIOSCordovaXLUpload('Fetch fallback PUT completed.', { status: response.status });
    return { status: response.status };
  } catch (error) {
    logIOSCordovaXLUpload('Fetch fallback PUT failed before receiving an HTTP response.');
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const uploadDocumentByPreSignedURL = async (
  preSignedUrl,
  file,
  fileMd5Base64,
  setStatementUploadProgress,
  setStatementLinearProgress,
  fileType,
  options = {}
) => {
  const { commonDocUpload = false } = options;
  const useIOSCordovaLargeFileTransport = isIOSCordovaLargePreSignedUpload(file);

  if (useIOSCordovaLargeFileTransport) {
    const contentType = file.type || 'application/octet-stream';

    logIOSCordovaXLUpload('Using iOS Cordova large-file upload transports.', {
      fileSize: file.size,
      contentType,
    });

    const nativeResult = await uploadIOSCordovaViaNativeHttp(
      preSignedUrl,
      file,
      fileMd5Base64,
      contentType
    );
    if (nativeResult.response || !nativeResult.shouldFallback) {
      return nativeResult.response;
    }

    const axiosResult = await uploadIOSCordovaViaAxios(
      preSignedUrl,
      file,
      fileMd5Base64,
      contentType,
      setStatementUploadProgress,
      setStatementLinearProgress,
      fileType,
      commonDocUpload
    );
    if (axiosResult.response || !axiosResult.shouldFallback) {
      return axiosResult.response;
    }

    return uploadIOSCordovaViaFetch(preSignedUrl, file, fileMd5Base64, contentType);
  }

  let response = null;
  try {
    response = await axios.put(preSignedUrl, file, {
      headers: {
        appid: 'sales',
        'Content-Type': 'multipart/form-data',
        'Content-MD5': fileMd5Base64,
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setStatementUploadProgress((prev) =>
          commonDocUpload ? { ...prev, [fileType]: percentCompleted } : percentCompleted
        );
        if (percentCompleted === 100) {
          setStatementLinearProgress((prev) =>
            commonDocUpload ? { ...prev, [fileType]: true } : true
          );
        }
      },
    });
  } catch (error) {
    return response;
  }

  return response;
};

const handleCancelUpload = (typeName, context, cancelData) => {
  if (typeName === 'act') {
    context.setCancelActupload(cancelData);
  }
  if (typeName === 'atm') {
    context.setCancelAtmupload(cancelData);
  }
  if (typeName === 'businessOwnerAddress') {
    context.setCancelBOAupload(cancelData);
  }
};
export const uploadDocChecklist = async (...args) => {
  const fileFormat = args[0];
  const fileName = args[1];
  const fileType = args[2];
  const formData = args[3];
  const accessToken = args[4];
  const typeName = args[5];
  const setProgress = args[6];
  const context = args[7];
  const initiateUiResponse = args[8];
  let response = null;
  const source = axios.CancelToken.source();
  const cancelToken = source.token;
  const cancelData = {
    source,
    typeName,
  };
  if (typeName === 'deedOfTrust') {
    context.setCancelDeed(cancelData);
  }
  if (typeName === 'articleIncorp') {
    context.setCancelArtIncorp(cancelData);
  }
  if (typeName === 'certIncorp') {
    context.setCancelCertIncorp(cancelData);
  }
  if (typeName === 'regCertificate') {
    context.setCancelRegCer(cancelData);
  }
  if (typeName === 'corpResolution') {
    context.setCancelCorpRes(cancelData);
  }
  if (typeName === 'listOfOfficers') {
    context.setCancelListOff(cancelData);
  }
  if (typeName === 'dbaResolution') {
    context.setCancelDbaRes(cancelData);
  }
  if (typeName === 'LlcResolution') {
    context.setCancelLlcRes(cancelData);
  }
  if (typeName === 'opAgreement') {
    context.setCancelOpAgree(cancelData);
  }
  if (typeName === 'certOfOrg') {
    context.setCancelCertOrg(cancelData);
  }
  if (typeName === 'certOfTrustReg') {
    context.setCancelTrustReg(cancelData);
  }
  if (typeName === 'businessAddress') {
    context.setCancelBAupload(cancelData);
  }
  if (typeName === 'certOfIncumbency') {
    context.setCancelIncumbency(cancelData);
  }
  if (typeName === 'act22') {
    context.setCancelAct22upload(cancelData);
  }
  handleCancelUpload(typeName, context, cancelData);
  try {
    response = await axios.post(
      `${initiateUiResponse.REACT_APP_DOCUMENT_UPLOAD_URL}?format=${fileFormat}&name=${fileName}&type=${fileType}`,
      formData,
      {
        cancelToken,
        headers: {
          appid: 'sales',
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${accessToken}`,
        },
        onUploadProgress: (ProgressEvent) => {
          const percentageCompleted = Math.round(
            (ProgressEvent.loaded * 100) / ProgressEvent.total
          );
          // controller.abort()
          setProgress((prevState) =>
            Object.assign({}, prevState, { [typeName]: percentageCompleted })
          );
        },
      }
    );
  } catch (e) {
    return response;
  }
  return response;
};
export const uploadDocXLDocCheckilst = async (...args) => {
  const fileFormat = args[0];
  const fileName = args[1];
  const fileType = args[2];
  const fileSize = args[3];
  const fileMd5Base64 = args[4];
  const accessToken = args[5];
  const typeName = args[6];
  const setProgress = args[7];
  const context = args[8];
  const initiateUiResponse = args[9];
  let response = null;
  const requestBody = {
    format: fileFormat,
    name: fileName,
    type: fileType,
    size: fileSize,
    contentMD5: fileMd5Base64,
  };
  const source = axios.CancelToken.source();
  const cancelToken = source.token;
  const cancelData = {
    source,
    typeName,
  };

  if (typeName === 'deedOfTrust') {
    context.setCancelDeed(cancelData);
  }
  if (typeName === 'corpResolution') {
    context.setCancelCorpRes(cancelData);
  }
  if (typeName === 'certIncorp') {
    context.setCancelCertIncorp(cancelData);
  }
  if (typeName === 'articleIncorp') {
    context.setCancelArtIncorp(cancelData);
  }
  if (typeName === 'listOfOfficers') {
    context.setCancelListOff(cancelData);
  }
  if (typeName === 'regCertificate') {
    context.setCancelRegCer(cancelData);
  }
  if (typeName === 'dbaResolution') {
    context.setCancelDbaRes(cancelData);
  }
  if (typeName === 'LlcResolution') {
    context.setCancelLlcRes(cancelData);
  }
  if (typeName === 'certOfOrg') {
    context.setCancelCertOrg(cancelData);
  }
  if (typeName === 'opAgreement') {
    context.setCancelOpAgree(cancelData);
  }
  if (typeName === 'certOfTrustReg') {
    context.setCancelTrustReg(cancelData);
  }
  if (typeName === 'certOfIncumbency') {
    context.setCancelIncumbency(cancelData);
  }
  if (typeName === 'businessAddress') {
    context.setCancelBAupload(cancelData);
  }
  if (typeName === 'act22') {
    context.setCancelAct22upload(cancelData);
  }
  handleCancelUpload(typeName, context, cancelData);
  try {
    response = await axios.post(
      `${initiateUiResponse.REACT_APP_DOCUMENT_UPLOAD_XL_URL}`,
      requestBody,
      {
        cancelToken,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          appid: SALES,
        },
        onUploadProgress: (progEvent) => {
          const percentageCompleted = Math.round((progEvent.loaded * 100) / progEvent.total);
          setProgress((prev) => Object.assign({}, prev, { [typeName]: percentageCompleted }));
        },
      }
    );
  } catch (e) {
    return response;
  }
  return response;
};

export const saveDocChecklist = async (docIds, context, flagCheck) => {
  const {
    accessToken,
    language,
    location,
    businessContactInformation,
    applicationId,
    applicantId,
    googleCID,
    savedDataFlow,
    initiateUiResponse,
  } = context;
  const formData = {
    Event: 'CaptureDocumentChecklist',
    Application: {
      ApplicationID: applicationId || null,
      ApplicantID: applicantId || null,
      TemplateVersion: '2021-11-24',
      Source: {
        Language: language,
        Location: location === 'VI' ? 'VI' : 'PR',
        UtmSource: null,
        UtmMedium: null,
        UtmCampaign: null,
        UtmContent: null,
        UtmTerm: null,
        BranchCode: null,
        ...(!savedDataFlow && { Google_CID: googleCID ?? '' }),
      },
      Business: {
        DocReUploadFlag: flagCheck,
        Type: businessContactInformation.businessType,
        ...(returnBusinessDocs(businessContactInformation.businessType, docIds).length > 0
          ? {
              BusinessDocuments: returnBusinessDocs(
                businessContactInformation.businessType,
                docIds
              ),
            }
          : {}),
      },
      Processing: {
        CaseReference: [],
      },
    },
  };
  let result = null;
  try {
    result = await axios.post(initiateUiResponse.REACT_APP_SAVE_SUBMIT_APPLICATION_URL, formData, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        appid: SALES,
        'Content-Type': 'application/json; charset=UTF-8',
      },
    });
  } catch (error) {
    return error.response;
  }
  return result;
};

const typeDBACheck = (selectedType, docIds) => {
  if (selectedType === 'DBA') {
    return [
      ...(docIds.dbaResolution
        ? [
            {
              DocType: 'DBA Resolution',
              DocId: docIds.dbaResolution,
            },
          ]
        : []),
      ...(docIds.regCertificate
        ? [
            {
              DocType: 'Commercial Registration Certificate',
              DocId: docIds.regCertificate,
            },
          ]
        : []),
    ];
  }
};
const typeCorporationPartnership = (selectedType, docIds) => {
  if (
    selectedType === 'Corporation' ||
    selectedType === 'Corporation DBA' ||
    selectedType === 'Partnership'
  ) {
    return [
      ...(docIds.corpResolution
        ? [
            {
              DocType: 'Corporate Resolution',
              DocId: docIds.corpResolution,
            },
          ]
        : []),
      ...(docIds.certIncorp
        ? [
            {
              DocType: 'Certificate of Incorporation',
              DocId: docIds.certIncorp,
            },
          ]
        : []),
      ...(docIds.articleIncorp
        ? [
            {
              DocType: 'Articles of Incorporation',
              DocId: docIds.articleIncorp,
            },
          ]
        : []),
      ...(docIds.listOfOfficers
        ? [
            {
              DocType: 'List of Directors and Officers',
              DocId: docIds.listOfOfficers,
            },
          ]
        : []),
      ...(docIds.regCertificate
        ? [
            {
              DocType: 'Commercial Registration Certificate',
              DocId: docIds.regCertificate,
            },
          ]
        : []),
    ];
  }
};
const typeLLCCheck = (selectedType, docIds) => {
  if (selectedType === 'LLC') {
    return [
      ...(docIds.LlcResolution
        ? [
            {
              DocType: 'LLC Resolution',
              DocId: docIds.LlcResolution,
            },
          ]
        : []),
      ...(docIds.certOfOrg
        ? [
            {
              DocType: 'Certificate of Organization',
              DocId: docIds.certOfOrg,
            },
          ]
        : []),
      ...(docIds.opAgreement
        ? [
            {
              DocType: 'Operational Agreement',
              DocId: docIds.opAgreement,
            },
          ]
        : []),
      ...(docIds.listOfOfficers
        ? [
            {
              DocType: 'List of Directors and Officers',
              DocId: docIds.listOfOfficers,
            },
          ]
        : []),
      ...(docIds.regCertificate
        ? [
            {
              DocType: 'Commercial Registration Certificate',
              DocId: docIds.regCertificate,
            },
          ]
        : []),
    ];
  }
};
const typeTrustCheck = (selectedType, docIds) => {
  if (selectedType === 'Trust') {
    return [
      ...(docIds.deedOfTrust
        ? [
            {
              DocType: 'Deed of Trust',
              DocId: docIds.deedOfTrust,
            },
          ]
        : []),
      ...(docIds.certOfTrustReg
        ? [
            {
              DocType: 'Certification for Trust Registry',
              DocId: docIds.certOfTrustReg,
            },
          ]
        : []),
      ...(docIds.certOfIncumbency
        ? [
            {
              DocType: 'Certificate of Incumbency',
              DocId: docIds.certOfIncumbency,
            },
          ]
        : []),
      ...(docIds.regCertificate
        ? [
            {
              DocType: 'Commercial Registration Certificate',
              DocId: docIds.regCertificate,
            },
          ]
        : []),
    ];
  }
};

const returnBusinessDocs = (selectedType, docIds) => {
  if (selectedType === 'DBA') {
    return typeDBACheck(selectedType, docIds);
  }
  if (
    selectedType === 'Corporation' ||
    selectedType === 'Corporation DBA' ||
    selectedType === 'Partnership'
  ) {
    return typeCorporationPartnership(selectedType, docIds);
  }
  if (selectedType === 'LLC') {
    return typeLLCCheck(selectedType, docIds);
  }
  if (selectedType === 'Trust') {
    return typeTrustCheck(selectedType, docIds);
  }
};

export const uploadLaterDocument = async (appId, accessToken, initiateUiResponse) => {
  let response = null;
  try {
    response = await axios.post(
      initiateUiResponse.REACT_APP_LINK_INITIAL_CALL,
      { applicationId: appId, docReUpload: 'true' },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          appid: SALES,
        },
      }
    );
  } catch (e) {
    return response;
  }
  return response;
};

export const saveDocChecklistUploadLater = async (
  docIds,
  context,
  docBusinessType,
  reUploadFlag,
  productName,
  initiateUiResponse
) => {
  const {
    accessToken,
    language,
    location,
    urlAppId,
    parentUploadData,
    grandParentUploadData,
    googleCID,
    savedDataFlow,
  } = context;
  const currentUrl = window.location.href;
  let formData;

  if (currentUrl.includes('retail-form')) {
    formData = {
      Event: 'CaptureCaeDocuments',
      Application: {
        ApplicationID: urlAppId || null,
        TemplateVersion: '2021-08-05',
        Source: {
          Language: language,
          Location: 'PR',
          UtmSource: null,
          UtmMedium: null,
          UtmCampaign: null,
          UtmContent: null,
          UtmTerm: null,
          BranchCode: null,
          ...(!savedDataFlow && { Google_CID: googleCID ?? '' }),
        },
        Products: [
          {
            ProductType: 'IRA',
            ProductName: productName,
          },
        ],
        Beneficiaries: [
          {
            Documents: [
              ...(parentUploadData?.parent
                ? [{ DocType: 'CAE Form', DocId: parentUploadData.parent }]
                : []),
              ...(grandParentUploadData?.grandParent
                ? [
                    {
                      DocType: 'Proof of guardianship',
                      DocId: grandParentUploadData.grandParent,
                    },
                  ]
                : []),
            ],
          },
        ],
        Processing: {
          CaseReference: [],
        },
      },
    };
  } else {
    formData = {
      Event: 'CaptureDocumentReUpload',
      Application: {
        ApplicationID: urlAppId || null,
        TemplateVersion: '2021-11-24',
        Source: {
          Language: language,
          Location: location === 'VI' ? 'VI' : 'PR',
          UtmSource: null,
          UtmMedium: null,
          UtmCampaign: null,
          UtmContent: null,
          UtmTerm: null,
          BranchCode: null,
          ...(!savedDataFlow && { Google_CID: googleCID ?? '' }),
        },
        Business: {
          Type: docBusinessType,
          DocReUploadFlag: reUploadFlag,
          ...(returnBusinessDocs(docBusinessType, docIds).length > 0
            ? { BusinessDocuments: returnBusinessDocs(docBusinessType, docIds) }
            : {}),
        },
        Processing: {
          CaseReference: [],
        },
      },
    };
  }

  try {
    const result = await axios.post(
      initiateUiResponse.REACT_APP_SAVE_SUBMIT_APPLICATION_URL,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          appid: SALES,
          'Content-Type': 'application/json; charset=UTF-8',
        },
      }
    );
    return result;
  } catch (error) {
    return error.response;
  }
};

// works for google drive upload in cordova devices
export const wcgenerateFileMD5 = (blob) =>
  new Promise((resolve, reject) => {
    console.log('wcgenerateFileMD5');
    const platform = getRuntimePlatform();

    if (!(blob instanceof Blob)) {
      const error = `[MD5] Input is not a Blob on ${platform}`;
      console.error(error, blob);
      reject(new Error(error));
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => {
      console.error(`[MD5] FileReader error on ${platform}`, reader.error);
      reject(reader.error);
    };

    reader.onload = () => {
      try {
        const buffer = reader.result;
        const wordArray = CryptoJS.lib.WordArray.create(buffer);
        const md5 = CryptoJS.MD5(wordArray).toString();
        resolve(md5);
      } catch (err) {
        console.error(`[MD5] Hashing failed on ${platform}`, err);
        reject(err);
      }
    };

    reader.readAsArrayBuffer(blob);
  });

export const generateFileMD5 = (file) =>
  new Promise((resolve, reject) => {
    console.log('generateFileMD5');
    const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice;
    const chunkSize = 2097152;
    const chunks = Math.ceil(file.size / chunkSize);

    let currentChunk = 0;
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();

    fileReader.onload = (e) => {
      spark.append(e.target.result);
      currentChunk += 1;

      if (currentChunk < chunks) {
        loadNext();
      } else {
        const hash = spark.end();
        resolve(hash);
      }
    };

    fileReader.onerror = () => {
      reject();
    };
    function loadNext() {
      const start = currentChunk * chunkSize;
      const end = start + chunkSize >= file.size ? file.size : start + chunkSize;
      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    }
    loadNext();
  });

export const generateFileMD5Cordova = async (blob) => {
  try {
    return await wcgenerateFileMD5(blob);
  } catch (err) {
    if (isCordovaFn()) {
      console.warn('[MD5] Skipping MD5 on Cordova due to failure', err);
      return null;
    }
    throw err;
  }
};
